/** @jsxImportSource @emotion/react */
import { useClickOutside, useKeydown } from '@btime/react-hooks'
import EmojiPicker, { Emoji } from 'emoji-picker-react'
import { COLORS } from '@btime/constants'
import { css } from '@emotion/css'
import { useTranslation } from 'react-i18next'
import React from 'react'

import Button from '../../packages/kiti/components/actions/Button'
import Icon from '../../packages/kiti/components/shapes/Icon'

const popoverStyles = css`
  position: relative;

  & .popover {
    position: absolute;
    top: calc(100% + 16px);
    left: -9px;
    border: 1px solid #b9bfda;
    border-radius: 9px;
    box-shadow: 0 6px 12px #b9bfda;
    z-index: 999;
    padding: 10px;
    background-color: #ffffff;

    & .button {
      margin-top: 10px;
      width: 100%;
    }
  }

  & .popover:before {
    content: '';
    border-style: solid;
    border-width: 0px 9px 10px;
    border-color: transparent transparent #b9bfda;
    position: absolute;
    top: -11px;
    left: 15px;
  }

  & .popover:after {
    content: '';
    border-style: solid;
    border-width: 0px 9px 10px;
    border-color: transparent transparent #ffffff;
    position: absolute;
    top: -10px;
    left: 15px;
  }
`

const inputStyles = css`
  display: flex;
  align-items: center;
  justify-content: start;
  cursor: pointer;
  width: fit-content;

  & span {
    line-height: 1 !important;
    width: fit-content !important;
    height: 32px !important;
    margin-left: -5px;
  }
`

const pickerStyles = css`
  & .EmojiPickerReact {
    --epr-emoji-size: 24px;
    --epr-hover-bg-color: ${COLORS.primary};
    --epr-category-icon-active-color: ${COLORS.primary};
    --epr-highlight-color: ${COLORS.purple};
    --epr-dark-category-icon-active-color: ${COLORS.primary};
    --epr-text-color: ${COLORS.dark1};
    --epr-picker-border-color: ${COLORS.light5};
    --epr-picker-border-radius: 5px;
    --epr-category-label-height: 32px;
    .epr-emoji-native {
      line-height: 25px !important;
    }
    .epr-category-nav {
      display: none;
    }
    .epr-emoji-category-label {
      font-size: 12px;
    }
  }
  & span {
    line-height: 25px !important;
  }
`

const hasIconPrefix = str => {
  const prefixes = ['bs', 'io', 'ri', 'fa']
  const lowerStr = str.toLowerCase()
  return prefixes.some(prefix => lowerStr.startsWith(prefix))
}

const renderEmojiPreview = value => {
  if (!value || hasIconPrefix(value)) {
    return <Icon name="BsEmojiSmile" size={2} color="light4" />
  }
  return <Emoji unified={value} emojiStyle="native" size={32} />
}

const previewConfig = {
  showPreview: false
}

const PopoverEmojiPicker = ({ onChange, value, ...props }) => {
  const { t } = useTranslation('components')
  const [isOpen, setIsOpen] = React.useState(false)
  const wrapperRef = React.useRef(null)

  const categories = [
    {
      category: 'smileys_people',
      name: t('emoji.smileysPeople')
    },
    {
      category: 'animals_nature',
      name: t('emoji.animalsNature')
    },
    {
      category: 'food_drink',
      name: t('emoji.foodDrink')
    },
    {
      category: 'travel_places',
      name: t('emoji.travelPlaces')
    },
    {
      category: 'activities',
      name: t('emoji.activities')
    },
    {
      category: 'objects',
      name: t('emoji.objects')
    },
    {
      category: 'symbols',
      name: t('emoji.symbols')
    }
  ]

  const close = React.useCallback(() => setIsOpen(false), [])
  useClickOutside(wrapperRef, { action: close })
  useKeydown(wrapperRef, { keys: ['Escape'], action: close })

  const handleChange = value => {
    onChange(value.unified)
    close()
  }

  const handleClear = () => {
    onChange(undefined)
  }

  return (
    <div className={popoverStyles}>
      <div className={inputStyles} onClick={() => setIsOpen(true)} role="button" aria-label={t('selectEmoji')}>
        {renderEmojiPreview(value)}
      </div>
      {isOpen && (
        <div className="popover" ref={wrapperRef}>
          <div className={pickerStyles}>
            <EmojiPicker
              previewConfig={previewConfig}
              onEmojiClick={handleChange}
              theme="light"
              emojiStyle="native"
              skinTonesDisabled={true}
              searchDisabled={true}
              width={400}
              height={250}
              categories={categories}
              {...props}
            />
          </div>
          <Button onClick={handleClear}>{t('clear')}</Button>
        </div>
      )}
    </div>
  )
}

export default PopoverEmojiPicker
